import React, { useState } from 'react';
import {
    Box, Card, CardContent, Typography, Stack, Button, TextField, Chip, Alert,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions
} from '@mui/material';
import {
    Send as SendIcon, Save as SaveIcon, RestartAlt as ResetIcon,
    Visibility as ShowIcon, SwapHoriz as ModeIcon, Bolt as RawIcon
} from '@mui/icons-material';
import deviceConfigService from '../../../services/deviceConfigService';

/**
 * Device mode & raw-command panel. Switches the TCU between RAW CAN and J1939
 * (each a SET CANM → SAVE CONFIG → RESET sequence per the supplier spec), plus
 * one-off SAVE CONFIG / RESET / SHOW CAN CFG and a free-text sender for any
 * command. This is where FOTA / version-check actions will be added later.
 */
const DeviceCommandPanel = ({ deviceId, onSent, onNotify, disabled }) => {
    const [freeText, setFreeText] = useState('');
    const [sending, setSending] = useState(false);
    const [confirm, setConfirm] = useState(null); // { title, body, commands, category }

    const run = async ({ commands, command, category }) => {
        try {
            setSending(true);
            const res = await deviceConfigService.sendCommand(deviceId, command ? { command, category } : { commands, category });
            onNotify?.(res.success ? `Sent ${res.sent?.length || 1} command(s)` : (res.message || 'Send failed'), res.success ? 'success' : 'error');
            if (res.success) onSent?.();
        } catch (err) {
            onNotify?.(err.response?.data?.message || err.message || 'Send failed', 'error');
        } finally { setSending(false); setConfirm(null); }
    };

    const ActionBtn = ({ icon, label, sub, color = 'primary', variant = 'outlined', onClick }) => (
        <Button variant={variant} color={color} startIcon={icon} disabled={disabled || sending} onClick={onClick}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, justifyContent: 'flex-start', px: 2, py: 1.25, flexDirection: 'column', alignItems: 'flex-start', minWidth: 200 }}>
            <span>{label}</span>
            {sub && <Typography variant="caption" sx={{ opacity: 0.7, fontWeight: 500, textTransform: 'none' }}>{sub}</Typography>}
        </Button>
    );

    return (
        <Card sx={{ borderRadius: '20px', mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={800} mb={0.5}>⚙️ Device Mode & Commands</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                    Switch the CAN decode mode or run a maintenance command. Mode switches send <code>SET CANM …</code> → <code>SAVE CONFIG</code> → <code>RESET</code> in sequence.
                </Typography>

                <Typography variant="subtitle2" fontWeight={800} mb={1} color="text.secondary">CAN MODE</Typography>
                <Stack direction="row" spacing={1.5} mb={2.5} flexWrap="wrap" useFlexGap>
                    <ActionBtn icon={<RawIcon />} variant="contained" color="primary"
                        label="Switch to RAW CAN" sub="SET CANM RAW · SAVE · RESET"
                        onClick={() => setConfirm({
                            title: 'Switch device to RAW CAN mode?',
                            body: 'The device will run SET CANM RAW, SAVE CONFIG, then RESET (it will reboot). Do this before applying a RAW CAN filter.',
                            commands: ['SET CANM RAW', 'SAVE CONFIG', 'RESET'], category: 'MODE'
                        })} />
                    <ActionBtn icon={<ModeIcon />}
                        label="Switch to J1939" sub="SET CANM J1939 · SAVE · RESET"
                        onClick={() => setConfirm({
                            title: 'Switch device to J1939 mode?',
                            body: 'The device will run SET CANM J1939, SAVE CONFIG, then RESET (it will reboot).',
                            commands: ['SET CANM J1939', 'SAVE CONFIG', 'RESET'], category: 'MODE'
                        })} />
                </Stack>

                <Typography variant="subtitle2" fontWeight={800} mb={1} color="text.secondary">MAINTENANCE</Typography>
                <Stack direction="row" spacing={1.5} mb={3} flexWrap="wrap" useFlexGap>
                    <ActionBtn icon={<ShowIcon />} label="Show CAN Config" sub="SHOW CAN CFG"
                        onClick={() => run({ command: 'SHOW CAN CFG', category: 'SHOW_CFG' })} />
                    <ActionBtn icon={<SaveIcon />} label="Save Config" sub="SAVE CONFIG"
                        onClick={() => run({ command: 'SAVE CONFIG', category: 'MODE' })} />
                    <ActionBtn icon={<ResetIcon />} color="error" label="Reset Device" sub="RESET (reboots)"
                        onClick={() => setConfirm({
                            title: 'Reset (reboot) the device?',
                            body: 'The device will restart. Any unsaved config is lost — run SAVE CONFIG first if needed.',
                            command: 'RESET', category: 'MODE'
                        })} />
                </Stack>

                <Typography variant="subtitle2" fontWeight={800} mb={1} color="text.secondary">RAW COMMAND</Typography>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <TextField fullWidth size="small" placeholder="Type any command, e.g. $CANCF,0,0,0,0,0,0,[0]#"
                        value={freeText} onChange={e => setFreeText(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && freeText.trim()) { run({ command: freeText.trim(), category: 'RAW' }); setFreeText(''); } }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'monospace', fontWeight: 600 } }} />
                    <Button variant="contained" startIcon={<SendIcon />} disabled={disabled || sending || !freeText.trim()}
                        onClick={() => { run({ command: freeText.trim(), category: 'RAW' }); setFreeText(''); }}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, whiteSpace: 'nowrap' }}>Send</Button>
                </Stack>

                {disabled && <Alert severity="warning" sx={{ mt: 2, borderRadius: '10px' }}>Backend is not connected to the MQTT broker — sending is disabled.</Alert>}

                <Dialog open={!!confirm} onClose={() => setConfirm(null)} PaperProps={{ sx: { borderRadius: '18px' } }}>
                    <DialogTitle sx={{ fontWeight: 800 }}>{confirm?.title}</DialogTitle>
                    <DialogContent>
                        <DialogContentText>{confirm?.body}</DialogContentText>
                        <Stack direction="row" spacing={0.5} mt={2} flexWrap="wrap" useFlexGap>
                            {(confirm?.commands || [confirm?.command]).filter(Boolean).map((c, i) => (
                                <Chip key={i} label={c} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700, borderRadius: '6px' }} />
                            ))}
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ p: 2.5 }}>
                        <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none', fontWeight: 700 }}>Cancel</Button>
                        <Button variant="contained" disabled={sending} onClick={() => run(confirm)}
                            sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px' }}>
                            {sending ? 'Sending…' : 'Confirm & Send'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </CardContent>
        </Card>
    );
};

export default DeviceCommandPanel;
